import { useMemo } from "react";
import type { FacetConfig, FacetCounts, FilterState, SearchConfig, SortState } from "../types/DataControls";
import { applyDataOperations } from "../utils/dataOperations";

export interface UseClientDataControlsOptions<T> {
  /** Raw data to process */
  data: T[];
  /** Filter state */
  filters?: FilterState;
  /** Sort state */
  sort?: SortState | null;
  /** Search configuration */
  search?: SearchConfig | null;
  /** Facet configurations */
  facetConfigs?: FacetConfig[];
}

export interface UseClientDataControlsResult<T> {
  /** Processed data (filtered, sorted, searched) */
  data: T[];
  /** Facet counts computed from filtered data */
  facetCounts: FacetCounts;
  /** Number of results after filtering/searching */
  resultCount: number;
}

/**
 * useClientDataControls
 *
 * Client-side hook for filtering, sorting, searching, and faceting data.
 * Uses memoization to avoid unnecessary recomputation.
 *
 * Operations are applied in this order:
 * 1. Search (fuzzy or exact matching)
 * 2. Filter (structured conditions)
 * 3. Facet computation (on filtered data)
 * 4. Sort
 *
 * @example
 * ```tsx
 * const { data, facetCounts, resultCount } = useClientDataControls({
 *   data: rawData,
 *   filters: createFilters([
 *     createFilter('department', 'in', ['Engineering', 'Sales'])
 *   ]),
 *   sort: { field: 'name', direction: 'asc' },
 *   search: { query: searchQuery, fields: ['name', 'email'] },
 *   facetConfigs: [
 *     { field: 'department', label: 'Department' },
 *     { field: 'status', label: 'Status' }
 *   ]
 * });
 * ```
 */
export function useClientDataControls<T>({
  data,
  filters,
  sort,
  search,
  facetConfigs = [],
}: UseClientDataControlsOptions<T>): UseClientDataControlsResult<T> {
  // Memoize the result to avoid unnecessary recalculations
  const result = useMemo(() => {
    return applyDataOperations({
      data,
      filters,
      sort,
      search,
      facetConfigs,
    });
  }, [data, filters, sort, search, facetConfigs]);

  return result;
}
